<script setup>
import { computed } from 'vue';
import { readBootstrap } from '../../shared/bootstrap.js';
import FooterAboutColumn from './FooterAboutColumn.vue';
import FooterBottomBar from './FooterBottomBar.vue';
import FooterContactColumn from './FooterContactColumn.vue';
import FooterLinksColumn from './FooterLinksColumn.vue';
import FooterPromoPopup from './FooterPromoPopup.vue';

const boot = readBootstrap(
  typeof document !== 'undefined' ? document.getElementById('vsb-vue-site-footer') : null,
  'vsb-site-footer-bootstrap'
);

const addressHtml = computed(() => boot.addressHtml || '');
const phone = computed(() => boot.phone || '');
const email = computed(() => boot.email || '');
const year = computed(() => Number(boot.year) || new Date().getFullYear());
const sitemapHref = computed(() => boot.sitemapHref || '/index.php?route=information/sitemap');

const social = computed(() => (Array.isArray(boot.social) ? boot.social : []));
const categoryLinks = computed(() => (Array.isArray(boot.categoryLinks) ? boot.categoryLinks : []));
const serviceLinks = computed(() => (Array.isArray(boot.serviceLinks) ? boot.serviceLinks : []));
const openingHours = computed(() => (Array.isArray(boot.openingHours) ? boot.openingHours : []));
const locations = computed(() => (Array.isArray(boot.locations) ? boot.locations : []));
const legalLinks = computed(() => (Array.isArray(boot.legalLinks) ? boot.legalLinks : []));

const aboutHeading = computed(() => boot.aboutHeading || 'Over VSB Verhuur');
const categoryColumnTitle = computed(() => boot.categoryColumnTitle || 'Hoofdcategorieën');
const serviceColumnTitle = computed(() => boot.serviceColumnTitle || 'Informatie & Service');
const copyrightBrand = computed(() => boot.copyrightBrand || 'VSB Verhuur');

const aboutIntro = computed(
  () =>
    boot.aboutIntro ||
    'Hét verhuurbedrijf van Culemborg en omgeving. Professioneel materiaal voor particulier en zakelijk!'
);

const promo = computed(() =>
  boot.promo && typeof boot.promo === 'object'
    ? boot.promo
    : {
        title: 'Klaar om weer vol gas te geven dit voorjaar?',
        text: 'Huur de machines en bedrijfswagens die jouw klus vooruit helpt!',
        ctaHref: 'https://vsbverhuur.nl',
        ctaLabel: 'Bekijk aanbod',
        iconSrc:
          'https://vsbverhuur.nl/image/cache/data/1234567890/e522d76d27e594a245caeb1d0b7165e3_l-600x320.jpg',
        iconAlt: 'Tuinmachines',
        iconTitle: 'Tuinmachines!'
      }
);

const showPromo = computed(() => boot.showPromo === true);
</script>

<template>
  <footer id="footer" class="vsb-site-footer">
    <div class="vsb-footer__main">
      <div class="content-container">
        <div class="vsb-footer__grid">
          <FooterContactColumn
            :address-html="addressHtml"
            :phone="phone"
            :email="email"
            :social="social"
          />
          <FooterLinksColumn :title="categoryColumnTitle" :links="categoryLinks" />
          <FooterLinksColumn :title="serviceColumnTitle" :links="serviceLinks" />
          <FooterAboutColumn
            :title="aboutHeading"
            :intro="aboutIntro"
            :hours="openingHours"
            :locations="locations"
          />
        </div>
      </div>
    </div>

    <FooterBottomBar
      :year="year"
      :sitemap-href="sitemapHref"
      :legal-links="legalLinks"
      :copyright-brand="copyrightBrand"
    />
  </footer>

  <FooterPromoPopup
    v-if="showPromo"
    :title="promo.title"
    :text="promo.text"
    :cta-href="promo.ctaHref"
    :cta-label="promo.ctaLabel"
    :icon-src="promo.iconSrc"
    :icon-alt="promo.iconAlt"
    :icon-title="promo.iconTitle"
  />
</template>

<style>
@import '../../shared/vsb-brand-colors.css';

#vsb-vue-site-footer,
#vsb-vue-site-footer * {
  box-sizing: border-box;
}

#vsb-vue-site-footer .content-container {
  min-width: 0 !important;
  width: 100% !important;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
}

.vsb-site-footer {
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  color: #fff;
  overflow-x: hidden;
  width: 100%;
  display: block !important;
}

.vsb-footer__main {
  padding: 60px 0 40px;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
}

.vsb-footer__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 50px;
  width: 100%;
}

.vsb-footer__column {
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 0;
}

.vsb-footer__heading {
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 15px;
  padding-bottom: 10px;
  border-bottom: 3px solid var(--vsb-blue-dark);
  position: relative;
}

.vsb-footer__heading::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 50px;
  height: 3px;
  background: linear-gradient(90deg, var(--vsb-blue-dark), var(--vsb-blue-mid));
}

.vsb-footer__intro {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #ccc;
  margin: 0;
}

.vsb-footer__links {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.vsb-footer__links a {
  color: #ccc;
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  display: inline-block;
  position: relative;
  padding-left: 15px;
}

.vsb-footer__links a::before {
  content: '▶';
  position: absolute;
  left: 0;
  color: var(--vsb-blue-mid);
  font-size: 0.7rem;
  transition: transform 0.3s ease;
}

.vsb-footer__links a:hover {
  color: #fff;
  padding-left: 20px;
}

.vsb-footer__links a:hover::before {
  transform: translateX(3px);
  color: var(--vsb-blue-light);
}

.vsb-footer__contact {
  list-style: none;
  margin: 0 0 20px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.vsb-footer__contact-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  color: #ccc;
  font-size: 0.95rem;
  line-height: 1.8;
}

.vsb-footer__contact-item i {
  color: var(--vsb-blue-mid);
  font-size: 1.3rem;
  min-width: 22px;
  margin-top: 3px;
}

.vsb-footer__contact-item strong {
  color: #fff;
  display: block;
  margin-bottom: 4px;
}

.vsb-footer__contact-item a {
  color: #7eb3e8;
  text-decoration: none;
  transition: color 0.3s ease;
}

.vsb-footer__contact-item a:hover {
  color: #fff;
}

.vsb-footer__subheading {
  font-size: 1rem;
  font-weight: 600;
  color: var(--vsb-blue-mid);
  margin: 20px 0 10px;
}

.vsb-footer__hours,
.vsb-footer__locations {
  list-style: none;
  margin: 0 0 15px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.vsb-footer__hours li,
.vsb-footer__locations li {
  color: #ccc;
  font-size: 0.9rem;
}

.vsb-footer__hours strong,
.vsb-footer__locations strong {
  color: #fff;
}

.vsb-footer__hours strong {
  min-width: 90px;
  display: inline-block;
}

.vsb-footer__social {
  display: flex;
  gap: 15px;
  margin-top: 10px;
}

.vsb-footer__social-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.2rem;
  text-decoration: none;
  transition: all 0.3s ease;
}

.vsb-footer__social-icon.facebook {
  background: #3b5998;
}

.vsb-footer__social-icon.instagram {
  background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
}

.vsb-footer__social-icon.linkedin {
  background: #0077b5;
}

.vsb-footer__social-icon.twitter {
  background: #1da1f2;
}

.vsb-footer__social-icon:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(20, 86, 184, 0.35);
}

.vsb-footer__bottom {
  background: #1a1a1a;
  border-top: 1px solid #333;
  padding: 25px 0;
}

.vsb-footer__bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.vsb-footer__copyright {
  color: #999;
  font-size: 0.9rem;
}

.vsb-footer__links-bottom {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.vsb-footer__links-bottom a {
  color: #999;
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

.vsb-footer__links-bottom a:hover {
  color: var(--vsb-blue-light);
}

.vsb-footer__separator {
  color: #666;
}

.vsb-footer__credits {
  color: #999;
  font-size: 0.85rem;
}

.vsb-footer__admix-link {
  color: var(--vsb-blue-mid);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.3s ease;
}

.vsb-footer__admix-link:hover {
  color: #fff;
}

html.vsb-footer--vue-ready #footer2 {
  display: none !important;
}

@media (max-width: 1200px) {
  .vsb-footer__grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
  }

  #vsb-vue-site-footer .content-container {
    padding: 0 30px;
  }
}

@media (max-width: 768px) {
  .vsb-footer__main {
    padding: 40px 0 30px;
  }

  #vsb-vue-site-footer .content-container {
    padding: 0 20px;
  }

  .vsb-footer__grid {
    grid-template-columns: 1fr;
    gap: 35px;
  }

  .vsb-footer__heading {
    font-size: 1.15rem;
  }

  .vsb-footer__heading::after {
    width: 40px;
  }

  .vsb-footer__bottom {
    padding: 20px 0;
  }

  .vsb-footer__bottom-content {
    flex-direction: column;
    text-align: center;
  }

  .vsb-footer__links-bottom {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .vsb-footer__main {
    padding: 30px 0 20px;
  }

  #vsb-vue-site-footer .content-container {
    padding: 0 15px;
  }

  .vsb-footer__heading {
    font-size: 1.05rem;
  }

  .vsb-footer__links a,
  .vsb-footer__contact-item,
  .vsb-footer__hours li,
  .vsb-footer__locations li {
    font-size: 0.9rem;
  }

  .vsb-footer__social-icon {
    width: 38px;
    height: 38px;
    font-size: 1.1rem;
  }
}
</style>
